import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Loader2, ClipboardList, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { formatSensorType } from '../../../shared/utils/formatters';
import { SensorTypeService } from '../../calibration/services/sensorType.service';
import { AcceptanceCriteriaService } from '../services/acceptanceCriteria.service';
import { formatMeasurementRangeLabel } from '../../calibration/utils/rangeUnit';

interface AcceptanceCriteriaEntry {
  id: string;
  sensorTypeId: string;
  category?: string;
  type?: string;
  model?: string;
  measurement_range?: number;
  acceptance_criteria: Record<string, number>;
}

const ManageAcceptanceCriteria: React.FC = () => {
  const navigate = useNavigate();
  const [, setAcceptanceCriteriaEntries] = useState<AcceptanceCriteriaEntry[]>([]);
  const [allEntries, setAllEntries] = useState<AcceptanceCriteriaEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<Record<string, number>>({});
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0,
  });
  const [totalItems, setTotalItems] = useState(0);

  // Load all data on mount
  useEffect(() => {
    loadAllData();
  }, []);

  // No need for separate page loading - ResponsiveTable handles it

  // Reset editing state when page changes. Intentionally only depends on paginationModel.page —
  // adding editingId here would re-fire this effect the moment editing starts and
  // immediately reset it again.
  useEffect(() => {
    if (editingId) {
      setEditingId(null);
      setEditFormData({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  const loadAllData = async () => {
    setIsLoading(true);
    try {
      // Load all sensor types
      const sensorTypes = await SensorTypeService.getAll();
      
      // Use acceptance criteria directly from sensor-types API response
      const entries: AcceptanceCriteriaEntry[] = sensorTypes.map((sensorType) => ({
        id: sensorType.id,
        sensorTypeId: sensorType.id,
        category: sensorType.category,
        type: sensorType.type,
        model: sensorType.model,
        measurement_range: sensorType.measurement_range,
        acceptance_criteria: sensorType.acceptance_criteria || {},
      }));
      
      setAllEntries(entries);
      setTotalItems(entries.length);
      setAcceptanceCriteriaEntries(entries);
    } catch (error: any) {
      console.error('Error loading acceptance criteria:', error);
      toast.error(error.message || 'Failed to load acceptance criteria');
      setAllEntries([]);
      setAcceptanceCriteriaEntries([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  };


  const handleEdit = (entry: AcceptanceCriteriaEntry) => {
    setEditingId(entry.id);
    setEditFormData({ ...entry.acceptance_criteria });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData({});
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;

    // Validate that all values are non-negative (backend requirement)
    const invalidEntries = Object.entries(editFormData).filter(([_, value]) => value < 0);
    if (invalidEntries.length > 0) {
      toast.error('All criteria values must be non-negative (>= 0)');
      return;
    }

    try {
      await AcceptanceCriteriaService.updateAcceptanceCriteria(editingId, editFormData);
      toast.success('Acceptance criteria updated successfully');
      setEditingId(null);
      setEditFormData({});
      // Reload all data
      await loadAllData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update acceptance criteria';
      toast.error(errorMessage);
    }
  };

  const handleCriteriaChange = (key: string, value: string) => {
    const numValue = parseFloat(value);
    // Backend requires values >= 0 (non-negative)
    // Only allow editing existing values, not adding or removing
    if (!isNaN(numValue) && numValue >= 0) {
      setEditFormData(prev => ({
        ...prev,
        [key]: numValue,
      }));
    }
    // If value is negative or invalid, ignore the change
  };


  const columns: GridColDef[] = [
    {
      field: 'row_number',
      headerName: '#',
      width: 60,
      sortable: false,
      filterable: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className="text-sm text-gray-500 font-medium">
          {paginationModel.page * paginationModel.pageSize + params.api.getRowIndexRelativeToVisibleRows(params.id) + 1}
        </div>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      flex: 1,
      minWidth: 120,
      sortable: false,
      filterable: true,
      renderCell: (params) => (
        <span className="text-sm font-medium text-gray-900">
          {params.value || 'N/A'}
        </span>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      minWidth: 140,
      sortable: false,
      filterable: true,
      renderCell: (params) => (
        <span className="text-sm text-gray-900">
          {formatSensorType(params.value)}
        </span>
      ),
    },
    {
      field: 'model',
      headerName: 'Model',
      flex: 0.9,
      minWidth: 120,
      sortable: false,
      filterable: true,
      renderCell: (params) => (
        <span className="text-sm text-gray-900">
          {params.value || 'N/A'}
        </span>
      ),
    },
    {
      field: 'measurement_range',
      headerName: 'Range',
      flex: 0.9,
      minWidth: 140,
      sortable: false,
      filterable: true,
      renderCell: (params) => {
        const row = params.row as AcceptanceCriteriaEntry;
        return (
          <span className="text-sm text-gray-900">
            {row.measurement_range != null
              ? formatMeasurementRangeLabel(row.measurement_range, undefined, row.category)
              : 'N/A'}
          </span>
        );
      },
    },
    {
      field: 'acceptance_criteria',
      headerName: 'Acceptance Criteria',
      flex: 2,
      minWidth: 300,
      sortable: false,
      filterable: true,
      renderCell: (params) => {
        const row = params.row as AcceptanceCriteriaEntry;
        if (editingId === row.id) {
          return (
            <div className="space-y-2 py-2">
              {Object.entries(editFormData).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={key}
                    readOnly
                    className="px-2 py-1 text-xs border border-gray-300 rounded bg-gray-50 flex-1"
                  />
                  <input
                    type="number"
                    value={value}
                    onChange={(e) => handleCriteriaChange(key, e.target.value)}
                    min="0"
                    step="0.01"
                    className="px-2 py-1 text-xs border border-gray-300 rounded w-20"
                  />
                </div>
              ))}
            </div>
          );
        }
        
        if (Object.keys(row.acceptance_criteria).length === 0) {
          return <span className="text-sm text-gray-400">No criteria defined</span>;
        }
        
        return (
          <div className="space-y-1">
            {Object.entries(row.acceptance_criteria).map(([key, value]) => (
              <div key={key} className="text-xs">
                <span className="font-medium text-gray-700">{key}:</span>{' '}
                <span className="text-gray-600">±{value}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 0.8,
      minWidth: 100,
      sortable: false,
      filterable: true,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row as AcceptanceCriteriaEntry;
        
        if (editingId === row.id) {
          return (
            <div className="flex items-center justify-center gap-2 h-full">
              <button
                onClick={handleSaveEdit}
                className="px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Save
              </button>
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          );
        }
        
        return (
          <button
            onClick={() => handleEdit(row)}
            title="Update"
            aria-label="Update"
            className="flex items-center justify-center p-1.5 text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100"
          >
            <Pencil className="h-4 w-4" />
          </button>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading acceptance criteria...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center space-x-2 text-sm text-gray-500 border-b border-gray-200 pb-4 mb-6">
        <button
          onClick={() => navigate('/')}
          className="hover:text-gray-700 transition-colors cursor-pointer"
          title="Back to Dashboard"
        >
          Dashboard
        </button>
        <ChevronRight className="h-4 w-4" />
        <span className="text-primary-600 font-medium">Manage Acceptance Criteria</span>
      </div>

      <div className="mb-6 flex items-start gap-3">
        <ClipboardList className="h-8 w-8 flex-shrink-0 text-primary-600 mt-0.5" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Acceptance Criteria</h1>
          <p className="mt-1 text-sm text-gray-600">
            View and update acceptance criteria for sensor types
          </p>
        </div>
      </div>

      {!isLoading && allEntries.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-gray-600">No acceptance criteria found.</p>
        </div>
      ) : (
        <DataGrid
          columns={columns}
          rows={allEntries}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={totalItems}
          loading={isLoading}
          paginationMode="client"
          getRowId={(row) => row.id}
          getRowHeight={() => {
            return 'auto'
          }}
          sx={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f3f4f6',
              borderBottom: '2px solid #d1d5db',
            },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '0.875rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#374151',
              padding: '12px 16px',
            },
            '& .MuiDataGrid-row': {
              borderBottom: '1px solid #e5e7eb',
              '&:hover': {
                backgroundColor: '#f9fafb',
              },
            },
            '& .MuiDataGrid-cell': {
              padding: '16px',
              fontSize: '0.875rem',
              color: '#111827',
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: '#f9fafb',
              borderTop: '1px solid #e5e7eb',
            },
            '& .MuiTablePagination-root': {
              fontSize: '0.875rem',
            },
          }}
        />
      )}
    </div>
  );
};

export default ManageAcceptanceCriteria;

